import { useState, useEffect, useMemo } from "react";
import { CollapsibleBar, SimpleTable } from "@components";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { getCustomers, getPaymentRequests } from "@api";
import { usePaymentRequestTemplatesContext } from "@context/PaymentRequestTemplatesContext";
import { buildApiQuery, formatAmount } from "@utils";

const PAGE_SIZE = 100;

const LIVE_STATUSES = [
  "Created",
  "Sent",
  "Delivered",
  "Opened",
  "Reminder sent",
  "Attempt failed",
  "Processing",
];

const accountsTableConfig = {
  columns: [
    {
      title: "Account",
      width: "40%",
      render: (r) => <span title={r.accountCode}>{r.accountCode}</span>,
    },
    {
      title: "Name",
      render: (r) => <span title={r.name}>{r.name}</span>,
    },
  ],
};

const templatesTableConfig = {
  columns: [
    {
      title: "Template name",
      render: (r) => <span title={r.name}>{r.name}</span>,
    },
  ],
};

const requestsTableConfig = {
  columns: [
    {
      title: "Account",
      width: "25%",
      render: (r) => <span title={r.reference}>{r.reference}</span>,
    },
    {
      title: "Name",
      width: "150px",
      render: (r) => (
        <span className="block truncate" title={r.customerName}>
          {r.customerName}
        </span>
      ),
    },
    {
      title: "Invoices",
      width: "25%",
      render: (r) => r.invoicesRequested ?? "",
    },
    {
      title: "Amount",
      width: "20%",
      render: (r) =>
        r.amountRequested != null ? formatAmount(r.amountRequested) : "",
    },
  ],
};

export default function ProfileDeleteWarning({
  profileId,
  defaultProfileName = "General",
}) {
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [accountCount, setAccountCount] = useState(0);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [paymentRequestCount, setPaymentRequestCount] = useState(0);
  const [openPanel, setOpenPanel] = useState(null);
  const { templates: allTemplates, loading: templatesLoading } =
    usePaymentRequestTemplatesContext();
  const templates = useMemo(
    () => allTemplates.filter((t) => t.paymentProfileId === profileId),
    [allTemplates, profileId],
  );

  const toggle = (panel) =>
    setOpenPanel((prev) => (prev === panel ? null : panel));

  useEffect(() => {
    if (!profileId) return;

    let cancelled = false;

    const fetchUsage = async () => {
      setLoading(true);

      const profileFilter = [
        { key: "paymentProfileId", type: "enum", value: [profileId] },
      ];

      try {
        const [customersRes, requestsRes] = await Promise.all([
          getCustomers(
            buildApiQuery({
              page: 1,
              pageSize: PAGE_SIZE,
              filters: profileFilter,
            }),
          ),
          getPaymentRequests(
            buildApiQuery({
              page: 1,
              pageSize: PAGE_SIZE,
              filters: [
                ...profileFilter,
                { key: "status", type: "enum", value: LIVE_STATUSES },
              ],
            }),
          ),
        ]);

        if (cancelled) return;

        setAccounts(customersRes?.data ?? []);
        setAccountCount(customersRes?.rows ?? 0);
        setPaymentRequests(requestsRes?.data ?? []);
        setPaymentRequestCount(requestsRes?.rows ?? 0);
      } catch (error) {
        console.error("Failed to fetch profile usage:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUsage();
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  if (loading || templatesLoading) {
    return (
      <div className="flex flex-col gap-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-10 rounded-lg bg-zinc-100 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (
    accountCount === 0 &&
    templates.length === 0 &&
    paymentRequestCount === 0
  ) {
    return null;
  }

  const icon = (
    <InformationCircleIcon className="size-4 shrink-0 text-zinc-400" />
  );

  return (
    <div className="flex-1 min-h-0 flex flex-col gap-2">
      {accountCount > 0 && (
        <CollapsibleBar
          icon={icon}
          borderColor="border-zinc-200"
          bgColor="bg-zinc-50"
          textColor="text-zinc-700"
          open={openPanel === "accounts"}
          onToggle={() => toggle("accounts")}
          summary={
            <>
              <strong>{accountCount}</strong>{" "}
              {accountCount === 1 ? "Account" : "Accounts"} will be assigned to{" "}
              <strong>{defaultProfileName}</strong>
            </>
          }
          count={accountCount}
        >
          <SimpleTable
            data={accounts}
            config={accountsTableConfig}
            noFooter
            rowHeight="sm"
            compact
            embedded
          />
        </CollapsibleBar>
      )}
      {templates.length > 0 && (
        <CollapsibleBar
          icon={icon}
          borderColor="border-zinc-200"
          bgColor="bg-zinc-50"
          textColor="text-zinc-700"
          open={openPanel === "templates"}
          onToggle={() => toggle("templates")}
          summary={
            <>
              <strong>{templates.length}</strong>{" "}
              {templates.length === 1 ? "Template" : "Templates"} will be
              assigned to <strong>{defaultProfileName}</strong>
            </>
          }
          count={templates.length}
        >
          <SimpleTable
            data={templates}
            config={templatesTableConfig}
            noFooter
            rowHeight="sm"
            compact
            embedded
          />
        </CollapsibleBar>
      )}
      {paymentRequestCount > 0 && (
        <CollapsibleBar
          icon={icon}
          borderColor="border-zinc-200"
          bgColor="bg-zinc-50"
          textColor="text-zinc-700"
          open={openPanel === "requests"}
          onToggle={() => toggle("requests")}
          summary={
            <>
              <strong>{paymentRequestCount}</strong> Payment{" "}
              {paymentRequestCount === 1 ? "Request" : "Requests"} will be
              assigned to <strong>{defaultProfileName}</strong>
            </>
          }
          count={paymentRequestCount}
        >
          <SimpleTable
            data={paymentRequests}
            config={requestsTableConfig}
            noFooter
            rowHeight="sm"
            compact
            embedded
          />
        </CollapsibleBar>
      )}
    </div>
  );
}
